// 최소사양 디버그 스위치 표 — 키·범위·설명의 유일한 정본. 부작용 없는 순수 데이터 모듈.
// 게임 쪽 판정(engine/debugFlags.js)과 편집 화면(screens/Admin/MinSpec.jsx)이 둘 다 이 표만 읽는다.
// ⚠ 편집 화면은 debugFlags.js 를 import 하면 안 된다 — 그 모듈은 로드 때 값을 한 번 굳힌다.
//
// 칸: key(localStorage 는 PREFIX + key) · kind 'bool'|'num' · dflt(저장값 없을 때 동작; num 은 null =
//   현행 동작) · min(최소사양 일괄 적용 값) · lo/hi(num 허용 범위, 밖이면 무시) · int(정수만) ·
//   label · what(무엇을 끄나) · account(줄이는 성능 계좌).

export const PREFIX = 'sg_dbg_';

export const MINSPEC_SWITCHES = [
  { key: 'minspec', kind: 'bool', dflt: false, min: true, label: '디버그 배지', account: '—',
    what: '게임 화면 오른쪽 아래에 FPS·드로콜·삼각형 배지를 띄운다' },
  { key: 'dprcap', kind: 'num', dflt: null, min: 0.5, lo: 0.25, hi: 4, label: '해상도 배율 상한',
    account: '오버드로·VRAM', what: '렌더 픽셀비를 이 값 이하로 — 0.5 면 픽셀 수 1/4' },
  { key: 'shadowmap', kind: 'bool', dflt: true, min: false, label: '그림자맵', account: '드로콜·삼각형·VRAM',
    what: '태양 그림자 깊이 패스(씬을 한 번 더 그린다)' },
  { key: 'postfx', kind: 'bool', dflt: true, min: false, label: '후처리 전체', account: '오버드로·VRAM',
    what: '블룸·SMAA·접지 그늘·갓레이·비네트·색보정 풀스크린 패스' },
  { key: 'water', kind: 'bool', dflt: true, min: false, label: '강·하천 수면',
    account: '삼각형·오버드로·CPU — ⚠ 게임 동작도 바뀐다(디버그 전용)',
    what: '수면 메시와 물 칸 색인(워커 90만 사각형). 끄면 물 칸 질의도 비어 물가가 마른 바닥으로 보이고 수중 안개도 꺼진다' },
  { key: 'clouds', kind: 'bool', dflt: true, min: false, label: '구름', account: '오버드로',
    what: '구름 지면 그림자 + 하늘 구름 디테일을 낮음으로' },
  { key: 'ambientfx', kind: 'bool', dflt: true, min: false, label: '분위기 이펙트', account: '드로콜·오버드로',
    what: '반딧불·나비·창불·석등·용마산 불꽃·잠실 달·동굴 빛줄기' },
  { key: 'farworld', kind: 'bool', dflt: true, min: false, label: '원경 지형', account: '삼각형(약 14만)',
    what: '지평선 원경 지형 한 장' },
  { key: 'viewcap', kind: 'num', dflt: null, min: 120, lo: 0, hi: 100000, label: '가시거리 상한(m)',
    account: '드로콜·삼각형', what: '건물·나무·지형 정밀 등 모든 종류별 거리의 기준값(게임 최소치 아래는 최소치로)' },
  { key: 'mobview', kind: 'num', dflt: null, min: 0, lo: 0, hi: 2, label: '몬스터·플레이어 거리 배율',
    account: '드로콜·CPU(스키닝)', what: '0 이면 몬스터·다른 플레이어·펫을 안 그린다' },
  { key: 'npcview', kind: 'num', dflt: null, min: 0.1, lo: 0, hi: 2, label: 'NPC 거리 배율',
    account: '드로콜·CPU', what: '0.1 이면 기준 120m 에서 12m 안 NPC 만 — 0 이면 대화도 못 한다' },
  { key: 'fxview', kind: 'num', dflt: null, min: 0, lo: 0, hi: 2, label: '이펙트·소품 거리 배율',
    account: '드로콜·오버드로', what: '수확 이펙트·워프 바닥링·월드 이름표·지면 소품·3D 잔디' },
  { key: 'partr', kind: 'num', dflt: null, min: 1, lo: 0, hi: 6, int: true, label: '파티션 청크 반경',
    account: 'CPU·메모리', what: '필지·도로를 받는 512m 칸 반경(기본 원경 켬 1 · 끔 3)' },
  { key: 'lodfade', kind: 'bool', dflt: true, min: false, label: 'LOD 크로스페이드', account: '드로콜',
    what: '상세↔임포스터 전환 0.35초 동안 둘을 겹쳐 그리기' },
  { key: 'lodhaze', kind: 'bool', dflt: true, min: false, label: '원경 공기원근', account: '—(셰이더 연산)',
    what: '원경 임포스터 안개 섞기' },
];

/** 저장 문자열 → 값(게임이 읽는 규칙). bool 은 `'0'` 만 꺼짐. num 은 parseFloat(int 면 parseInt)
 *  결과가 [lo, hi] 안이어야 값이고 아니면 null(= 기본 동작). */
export function parseSwitch(sw, raw) {
  if (raw === null || raw === undefined) return sw.dflt;
  if (sw.kind === 'bool') return raw !== '0';
  if (String(raw).trim() === '') return null;
  const n = sw.int ? parseInt(raw, 10) : parseFloat(raw);
  return Number.isFinite(n) && n >= sw.lo && n <= sw.hi ? n : null;
}

/** 편집 화면 입력 검사 — 숫자 전체가 숫자여야 하고(`2abc` 거절) 범위·정수 조건을 지켜야 한다. */
export function isValidNumInput(sw, text) {
  const n = Number(text);
  if (String(text).trim() === '' || !Number.isFinite(n)) return false;
  if (sw.int && !Number.isInteger(n)) return false;
  return n >= sw.lo && n <= sw.hi;
}

/** 값 → 저장 문자열. bool 은 '1'/'0'. */
export const encodeSwitch = (sw, v) => (sw.kind === 'bool' ? (v ? '1' : '0') : String(v));

/** key → 표의 한 줄 */
export const MINSPEC_BY_KEY = Object.fromEntries(MINSPEC_SWITCHES.map((sw) => [sw.key, sw]));
